/**
 * Simple i18n utility for WebSignPanel
 * Supports Spanish (es) and Basque (eu)
 */

const translations = {
  es: {
    // Action bar buttons
    'actionBar.cancel': 'Cancelar',
    'actionBar.print': 'Imprimir',
    'actionBar.accept': 'Diferir firma',
    'actionBar.finish': 'Terminar',
    'actionBar.finishAndPrint': 'Aceptar e Imprimir',
    'actionBar.viewPdf': 'Ver PDF',

    // Generic buttons (dialogs, signer list)
    'button.cancel': 'Cancelar',
    'button.print': 'Imprimir',
    'button.close': 'Cerrar',

    // PDF modal
    'pdfModal.title': 'Documento',
    'pdfModal.download': 'Descargar',
    'pdfModal.prevPage': 'Página anterior',
    'pdfModal.nextPage': 'Página siguiente',
    'pdfModal.pageOf': '{page} de {total}',
    'button.sign': 'Firmar',
    
    // Signer list
    'signers.title': 'Firmantes',
    'signers.mandatory': 'Obligatorio',
    'signers.signed': 'Firmado',
    'signers.pending': 'Pendiente',
    'signers.defaultGroup': 'Grupo Principal',
    'signers.defaultLabel': 'Firmante',
    
    // Signer image sidebar
    'signerImage.title': 'Información del firmante',
    'signerImage.photo': 'Foto',
    'signerImage.signature': 'Firma',
    
    // Signature status
    'status.signed': 'Firmado',
    'status.pending': 'Pendiente de firma',
    
    // Dialogs and confirmations
    'confirm.cancel': '¿Está seguro de que desea cancelar el proceso de firma?',
    'confirm.mandatoryRequired': 'Debe completar todas las firmas obligatorias antes de aceptar.',
    'confirm.printTitle': 'Firmar en papel',
    'confirm.print': '¿Está seguro de que desea imprimir el documento para firmarlo físicamente? Esta acción enviará el documento a la impresora predeterminada.',
    
    // Errors
    'error.signature': 'Error capturing signature',
    'error.unknown': 'Error desconocido',
    'error.noPdf': 'No hay documento PDF disponible para firmar',
    'error.print': 'Error al imprimir el documento',
    'error.download': 'Error al descargar el documento',
    'error.bridgeRequired': 'Es necesario tener en ejecución WebSign Bridge para firmar',
    'bridge.open': 'Abrir Bridge',
    
    // PDF Viewer
    'pdf.loading': 'Cargando documento...',
    'pdf.error': 'Error al cargar el documento',
    'pdf.noPdf': 'No hay documento PDF cargado',
    
    // Mobile menu
    'menu.open': 'Abrir menú',
    'menu.close': 'Cerrar menú'
  },
  
  eu: {
    // Action bar buttons
    'actionBar.cancel': 'Ezeztatu',
    'actionBar.print': 'Inprimatu',
    'actionBar.accept': 'Sinadura atzeratu',
    'actionBar.finish': 'Amaitu',
    'actionBar.finishAndPrint': 'Onartu eta Inprimatu',
    'actionBar.viewPdf': 'PDFa ikusi',

    // Generic buttons (dialogs, signer list)
    'button.cancel': 'Ezeztatu',
    'button.print': 'Inprimatu',
    'button.close': 'Itxi',

    // PDF modal
    'pdfModal.title': 'Dokumentua',
    'pdfModal.download': 'Deskargatu',
    'pdfModal.prevPage': 'Aurreko orria',
    'pdfModal.nextPage': 'Hurrengo orria',
    'pdfModal.pageOf': '{page} / {total}',
    'button.sign': 'Sinatu',
    
    // Signer list
    'signers.title': 'Sinatzaileak',
    'signers.mandatory': 'Derrigorrezkoa',
    'signers.signed': 'Sinatuta',
    'signers.pending': 'Zain',
    'signers.defaultGroup': 'Talde Nagusia',
    'signers.defaultLabel': 'Sinatzailea',
    
    // Signer image sidebar
    'signerImage.title': 'Sinatzailearen informazioa',
    'signerImage.photo': 'Argazkia',
    'signerImage.signature': 'Sinadura',
    
    // Signature status
    'status.signed': 'Sinatuta',
    'status.pending': 'Sinadura zain',
    
    // Dialogs and confirmations
    'confirm.cancel': 'Ziur zaude sinadura prozesua ezeztatu nahi duzula?',
    'confirm.mandatoryRequired': 'Osatu sinadura guztiak prozesua amaitu aurretik.',
    'confirm.printTitle': 'Paperean sinatu',
    'confirm.print': 'Ziur zaude dokumentua inprimatu nahi duzula fisikoki sinatzeko? Ekintza honek dokumentua inprimagailu lehenetsira bidaliko du.',
    
    // Errors
    'error.signature': 'Errorea sinadura hartzean',
    'error.unknown': 'Errore ezezaguna',
    'error.noPdf': 'Ez dago PDF dokumenturik sinatzeko',
    'error.print': 'Errorea dokumentua inprimatzean',
    'error.download': 'Errorea dokumentua deskargatzean',
    'error.bridgeRequired': 'WebSign Bridge irekita eduki behar da sinatzeko',
    'bridge.open': 'Bridge ireki',
    
    // PDF Viewer
    'pdf.loading': 'Dokumentua kargatzen...',
    'pdf.error': 'Errorea dokumentua kargatzean',
    'pdf.noPdf': 'Ez dago PDF dokumenturik kargatuta',
    
    // Mobile menu
    'menu.open': 'Menua ireki',
    'menu.close': 'Menua itxi'
  }
};

function escapeHtml(text) {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

class I18n {
  constructor() {
    this.language = 'es';
    this.overrides = {};
  }

  /**
   * Set custom texts that take precedence over the built-in translations.
   * Only non-empty string values are used; anything else is ignored.
   * Values are HTML-escaped because texts are rendered via innerHTML.
   * @param {Object} labels - Map of translation key -> custom text
   */
  setOverrides(labels) {
    this.overrides = {};
    if (!labels || typeof labels !== 'object') return;

    Object.entries(labels).forEach(([key, value]) => {
      if (typeof value === 'string' && value !== '') {
        this.overrides[key] = escapeHtml(value);
      }
    });
  }

  /**
   * Set the current language
   * @param {string} lang - Language code ('es' or 'eu')
   */
  setLanguage(lang) {
    if (translations[lang]) {
      this.language = lang;
    } else {
      console.warn(`Language '${lang}' not supported, falling back to 'es'`);
      this.language = 'es';
    }
  }

  /**
   * Get translation for a key
   * @param {string} key - Translation key
   * @param {Object} params - Optional parameters for interpolation
   * @returns {string} Translated string
   */
  t(key, params = {}) {
    const langTranslations = translations[this.language] || translations.es;
    let text = this.overrides[key] || langTranslations[key] || translations.es[key] || key;

    // Simple parameter interpolation: {name} -> value
    Object.entries(params).forEach(([param, value]) => {
      text = text.replace(new RegExp(`\\{${param}\\}`, 'g'), value);
    });

    return text;
  }

  /**
   * Get current language
   * @returns {string} Current language code
   */
  getLanguage() {
    return this.language;
  }
}

// Singleton instance
const i18n = new I18n();

export { i18n, I18n };
